import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { BookingSettings, Appointment, BookingLink, TimeSlot, AvailableSlot } from '../types/appointments';
import { googleCalendarService } from './googleCalendarService';
import { emailService } from './emailService';

class AppointmentService {
  private bookingSettingsCollection = 'bookingSettings';
  private appointmentsCollection = 'appointments';
  private bookingLinksCollection = 'bookingLinks';

  // Booking Settings CRUD
  async createBookingSettings(settings: Omit<BookingSettings, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, this.bookingSettingsCollection), {
        ...settings,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error creating booking settings:', error);
      throw error;
    }
  }

  async updateBookingSettings(id: string, settings: Partial<BookingSettings>): Promise<void> {
    try {
      const docRef = doc(db, this.bookingSettingsCollection, id);
      await updateDoc(docRef, {
        ...settings,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating booking settings:', error);
      throw error;
    }
  }

  async getBookingSettings(organizationId: string): Promise<BookingSettings[]> {
    try {
      const q = query(
        collection(db, this.bookingSettingsCollection),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as BookingSettings));
    } catch (error) {
      console.error('Error fetching booking settings:', error);
      throw error;
    }
  }

  async getBookingSettingsById(id: string): Promise<BookingSettings | null> {
    try {
      const docRef = doc(db, this.bookingSettingsCollection, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data(),
          createdAt: docSnap.data().createdAt?.toDate(),
          updatedAt: docSnap.data().updatedAt?.toDate(),
        } as BookingSettings;
      }
      return null;
    } catch (error) {
      console.error('Error fetching booking settings:', error);
      throw error;
    }
  }

  async deleteBookingSettings(id: string): Promise<void> {
    try {
      const docRef = doc(db, this.bookingSettingsCollection, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting booking settings:', error);
      throw error;
    }
  }

  // Booking Links
  async createBookingLink(settingsId: string): Promise<BookingLink> {
    try {
      const publicId = this.generatePublicId();
      const linkData = {
        settingsId,
        publicId,
        isActive: true,
        createdAt: serverTimestamp(),
      };
      
      const docRef = await addDoc(collection(db, this.bookingLinksCollection), linkData);
      
      return {
        id: docRef.id,
        settingsId,
        publicId,
        isActive: true,
        createdAt: new Date(),
      };
    } catch (error) {
      console.error('Error creating booking link:', error);
      throw error;
    }
  }

  async getBookingLinkByPublicId(publicId: string): Promise<BookingLink | null> {
    try {
      const q = query(
        collection(db, this.bookingLinksCollection),
        where('publicId', '==', publicId),
        where('isActive', '==', true)
      );
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate(),
        } as BookingLink;
      }
      return null;
    } catch (error) {
      console.error('Error fetching booking link:', error);
      throw error;
    }
  }

  // Appointments CRUD
  async createAppointment(appointment: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>, organizerEmail?: string): Promise<string> {
    try {
      // Először létrehozzuk az appointment-et
      const docRef = await addDoc(collection(db, this.appointmentsCollection), {
        ...appointment,
        appointmentDate: Timestamp.fromDate(appointment.appointmentDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const appointmentId = docRef.id;

      // Ha online meeting és van organizerEmail, próbáljuk meg Google Calendar-t használni
      if (appointment.meetingType === 'online' && organizerEmail) {
        try {
          const appointmentWithId: Appointment = {
            ...appointment,
            id: appointmentId,
            appointmentDate: appointment.appointmentDate,
            createdAt: new Date(),
            updatedAt: new Date()
          };

          const googleEvent = await googleCalendarService.createEventWithMeet(appointmentWithId, organizerEmail);
          
          if (googleEvent && googleEvent.meetLink) {
            // Frissítjük az appointment-et a Google Calendar adatokkal
            await updateDoc(docRef, {
              meetingLink: googleEvent.meetLink,
              calendarEventId: googleEvent.eventId,
              meetingInstructions: `Google Meet link: ${googleEvent.meetLink}`,
              updatedAt: serverTimestamp()
            });
            
            // Frissítjük a helyi appointment objektumot az email küldéshez
            appointment.meetingLink = googleEvent.meetLink;
            appointment.meetingInstructions = `Google Meet link: ${googleEvent.meetLink}`;
          }
        } catch (googleError) {
          console.error('Error creating Google Calendar event:', googleError);
          // Ne dobjunk hibát, csak loggoljuk - az appointment létrejött
        }
      }

      // Email küldés a foglalás megerősítéséről
      try {
        const settingsDoc = await getDoc(doc(db, this.bookingSettingsCollection, appointment.bookingSettingsId));
        const settings = settingsDoc.data() as BookingSettings;

        if (settings) {
          await emailService.sendNotification({
            to: appointment.clientEmail,
            subject: `Időpont foglalás megerősítése - ${settings.title}`,
            message: this.generateAppointmentEmailContent(appointment, settings),
            organizationId: appointment.organizationId,
            fromName: 'YouCRM Időpontfoglalás'
          });
        }
      } catch (emailError) {
        console.error('Error sending appointment confirmation email:', emailError);
        // Ne dobjunk hibát, az appointment létrejött
      }

      return appointmentId;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  }

  async getAppointments(organizationId: string, startDate?: Date, endDate?: Date): Promise<Appointment[]> {
    try {
      let q = query(
        collection(db, this.appointmentsCollection),
        where('organizationId', '==', organizationId),
        orderBy('appointmentDate', 'asc')
      );

      const querySnapshot = await getDocs(q);
      let appointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        appointmentDate: doc.data().appointmentDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      } as Appointment));

      // Kliens oldali szűrés dátum alapján
      if (startDate || endDate) {
        appointments = appointments.filter(apt => {
          const aptDate = apt.appointmentDate;
          if (startDate && aptDate < startDate) return false;
          if (endDate && aptDate > endDate) return false;
          return true;
        });
      }

      return appointments;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  }

  async updateAppointment(id: string, appointment: Partial<Appointment>, organizerEmail?: string): Promise<void> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      
      // Alapvető frissítés
      const updateData: any = {
        ...appointment,
        updatedAt: serverTimestamp(),
      };

      // Ha van appointmentDate, konvertáljuk Timestamp-re
      if (appointment.appointmentDate) {
        updateData.appointmentDate = Timestamp.fromDate(appointment.appointmentDate);
      }

      await updateDoc(docRef, updateData);

      // Ha online meeting és van Google Calendar integráció és calendarEventId
      if (appointment.meetingType === 'online' && organizerEmail) {
        try {
          // Betöltjük a teljes appointment-et a calendarEventId-ért
          const appointmentDoc = await getDoc(docRef);
          const fullAppointment = appointmentDoc.data() as Appointment;

          if (fullAppointment.calendarEventId) {
            const appointmentWithId: Appointment = {
              ...fullAppointment,
              ...appointment,
              id: id,
              appointmentDate: appointment.appointmentDate || fullAppointment.appointmentDate,
            };

            const googleEvent = await googleCalendarService.updateEvent(
              fullAppointment.calendarEventId, 
              appointmentWithId, 
              organizerEmail
            );
            
            if (googleEvent) {
              // Frissítjük az appointment-et az új Google Calendar adatokkal
              await updateDoc(docRef, {
                meetingLink: googleEvent.meetLink,
                meetingInstructions: `Google Meet link: ${googleEvent.meetLink}\nCalendar esemény: ${googleEvent.calendarLink}`,
                updatedAt: serverTimestamp()
              });
            }
          }
        } catch (googleError) {
          console.error('Error updating Google Calendar event:', googleError);
          // Ne dobjunk hibát, csak loggoljuk - az appointment frissült
        }
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  }

  async updateAppointmentStatus(id: string, status: Appointment['status']): Promise<void> {
    try {
      const docRef = doc(db, this.appointmentsCollection, id);
      await updateDoc(docRef, {
        status,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }
  }

  // Utility Methods
  private generatePublicId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  generateBookingUrl(publicId: string): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/book/${publicId}`;
  }

  generateQRCode(url: string): string {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}`;
  }

  // Available Slots Calculation
  getAvailableSlots(settings: BookingSettings, date: Date, existingAppointments: Appointment[]): AvailableSlot[] {
    const slots: AvailableSlot[] = [];
    const dayOfWeek = date.getDay();
    
    // Ellenőrizzük, hogy a nap elérhető-e
    if (!settings.availableHours.days.includes(dayOfWeek)) {
      return slots;
    }

    const [startHour, startMinute] = settings.availableHours.start.split(':').map(Number);
    const [endHour, endMinute] = settings.availableHours.end.split(':').map(Number);
    
    const startTime = new Date(date);
    startTime.setHours(startHour, startMinute, 0, 0);
    
    const endTime = new Date(date);
    endTime.setHours(endHour, endMinute, 0, 0);
    
    const slotDuration = settings.duration + settings.bufferTime;
    
    let currentTime = new Date(startTime);
    
    while (currentTime < endTime) {
      const slotEnd = new Date(currentTime.getTime() + settings.duration * 60000);
      
      if (slotEnd <= endTime) {
        // Ellenőrizzük, hogy nincs-e már foglalás
        const isBooked = existingAppointments.some(apt => {
          const aptStart = apt.appointmentDate;
          const aptEnd = new Date(aptStart.getTime() + apt.duration * 60000);
          
          return (currentTime >= aptStart && currentTime < aptEnd) ||
                 (slotEnd > aptStart && slotEnd <= aptEnd) ||
                 (currentTime <= aptStart && slotEnd >= aptEnd);
        });

        // Ellenőrizzük a minimum előrejelzési időt
        const now = new Date();
        const minAdvanceTime = new Date(now.getTime() + settings.advanceBooking.min * 60 * 60 * 1000);
        const isInFuture = currentTime >= minAdvanceTime;

        slots.push({
          time: currentTime.toLocaleTimeString('hu-HU', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          datetime: new Date(currentTime),
          isAvailable: !isBooked && isInFuture,
        });
      }
      
      currentTime = new Date(currentTime.getTime() + slotDuration * 60000);
    }
    
    return slots;
  }

  isDateAvailable(settings: BookingSettings, date: Date): boolean {
    const dayOfWeek = date.getDay();
    const now = new Date();
    const maxAdvanceDate = new Date(now.getTime() + settings.advanceBooking.max * 24 * 60 * 60 * 1000);
    
    return settings.availableHours.days.includes(dayOfWeek) && 
           date >= now && 
           date <= maxAdvanceDate;
  }

  // Email content generator
  private generateAppointmentEmailContent(appointment: Appointment, settings: BookingSettings): string {
    const dateStr = appointment.appointmentDate.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });

    const timeStr = appointment.appointmentTime || appointment.appointmentDate.toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit'
    });

    const statusText = appointment.status === 'pending' 
      ? '<strong style="color: #f59e0b;">⏳ Megerősítésre vár</strong>' 
      : '<strong style="color: #10b981;">✅ Megerősítve</strong>';

    const meetingInfo = appointment.meetingType === 'online' && appointment.meetingLink
      ? `<div style="margin: 20px 0; padding: 15px; background-color: #e0f2fe; border-left: 4px solid #0284c7; border-radius: 4px;">
          <h3 style="margin: 0 0 10px 0; color: #0c4a6e;">🎥 Online találkozó</h3>
          <p style="margin: 5px 0;"><strong>Meeting link:</strong></p>
          <a href="${appointment.meetingLink}" style="color: #0284c7; text-decoration: none; font-weight: 500; word-break: break-all;">${appointment.meetingLink}</a>
          ${appointment.meetingInstructions ? `<p style="margin: 10px 0 0 0; font-size: 14px; color: #475569;">${appointment.meetingInstructions}</p>` : ''}
        </div>`
      : appointment.meetingType === 'in_person'
        ? `<div style="margin: 20px 0; padding: 15px; background-color: #f0fdf4; border-left: 4px solid #22c55e; border-radius: 4px;">
            <p style="margin: 0; color: #166534;">📍 Személyes találkozó</p>
          </div>`
        : '';

    return `Kedves ${appointment.clientName}!<br><br>
      Köszönjük időpontfoglalását!<br><br>
      <div style="background-color: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0;">
        <h2 style="margin: 0 0 15px 0; color: #1e293b;">📅 Foglalás részletei</h2>
        <p style="margin: 8px 0;"><strong>Szolgáltatás:</strong> ${settings.title}</p>
        <p style="margin: 8px 0;"><strong>Dátum:</strong> ${dateStr}</p>
        <p style="margin: 8px 0;"><strong>Időpont:</strong> ${timeStr}</p>
        <p style="margin: 8px 0;"><strong>Időtartam:</strong> ${appointment.duration} perc</p>
        <p style="margin: 8px 0;"><strong>Státusz:</strong> ${statusText}</p>
      </div>
      ${meetingInfo}
      ${appointment.status === 'pending' ? '<p style="color: #f59e0b; font-weight: 500;">⚠️ Kérjük várja meg a foglalás megerősítését!</p>' : ''}
      <p style="margin-top: 20px;">Ha bármilyen kérdése van, keressen minket bizalommal.</p>`;
  }
}

export const appointmentService = new AppointmentService();
